import React, { useState, useRef, useEffect } from 'react';
import { Wrench } from 'lucide-react';
import { Link } from '../Link';
import { Tooltip } from '../Tooltip';
import type { IntlShape } from 'react-intl';

export interface Tool {
  route: string;
  label: string;
}

export interface ToolsMenuProps {
  intl: IntlShape;
  currentToolRoute: string;
  tools: Tool[];
  className?: string;
}

/**
 * Tools menu component for navigation
 * Shows tools dropdown on mobile/tablet, ToolSwitcher on desktop
 */
export const ToolsMenu: React.FC<ToolsMenuProps> = ({
  intl,
  currentToolRoute,
  tools,
  className = '',
}) => {
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const toolsMenuRef = useRef<HTMLDivElement>(null);

  // Close tools menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target as Node)) {
        setIsToolsMenuOpen(false);
      }
    };
    if (isToolsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isToolsMenuOpen]);

  const normalizePath = (path: string) => {
    if (path === '/') return path;
    return path.replace(/\/$/, '');
  };

  const normalizedCurrentTool = normalizePath(currentToolRoute);

  return (
    <div ref={toolsMenuRef} className={`2xl:hidden flex-1 flex justify-center relative ${className}`}>
      <Tooltip
        content={intl.formatMessage({
          id: 'tooltip.tools',
          defaultMessage: 'Access job search tools and utilities',
          description: 'Tooltip text for tools button that opens job search tools menu',
        })}
        position="bottom"
      >
        <button
          onClick={() => setIsToolsMenuOpen(!isToolsMenuOpen)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          aria-label={intl.formatMessage({
            id: 'tools.title.aria',
            defaultMessage: 'Tools',
            description: 'Accessibility label for tools button',
          })}
        >
          <Wrench className="w-4 h-4" />
          <span>{intl.formatMessage({ id: 'tools.title.button', defaultMessage: 'Tools', description: 'Button label for tools section' })}</span>
        </button>
      </Tooltip>
      {isToolsMenuOpen && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 max-h-[80vh] overflow-y-auto">
          {tools.map((tool) => {
            const normalizedRoute = normalizePath(tool.route);
            const isActive = normalizedCurrentTool === normalizedRoute;
            return (
              <Link
                key={tool.route}
                href={tool.route}
                onClick={() => setIsToolsMenuOpen(false)}
                className={`block px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-green-100 text-green-900 dark:bg-green-200 dark:text-black font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {tool.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
